import { RadioButtons } from '@dapp/multi-lib/sdaoui/RadioButtons';
import {
  VaultAssetTypeFilter,
  VaultChainFilter,
  VaultStatusFilter,
} from 'structure/pv2/dynavault/types';
import { ViewTypeSwitch } from 'structure/pv2/dynavault/components/ViewTypeSwitch';
import { useVaultListContext } from 'structure/pv2/dynavault/context/VaultListContext';
import { Button } from '@dapp/multi-lib/sdaoui/Button';
import { cx } from 'class-variance-authority';
import { Icon } from '@dapp/multi-lib/sdaoui/Icon';
import { Body3 } from 'components/Typography';
import useIsMobile from '@dapp/multi-lib/ui/shared-hooks/useIsMobile';

export const VaultListControls = () => {
  const {
    vaultStatusFilter,
    setVaultStatusFilter,
    vaultChainFilter,
    setVaultChainFilter,
    vaultAssetTypeFilter,
    setVaultAssetTypeFilter,
    viewType,
  } = useVaultListContext();

  const isMobile = useIsMobile();

  return (
    <div className='lg:mt-15 mt-9 flex flex-col gap-4'>
      <div className='flex w-full flex-wrap items-end gap-4'>
        <div className='flex flex-col gap-2'>
          <Body3 className='text-nowrap text-white-500'>Status</Body3>
          <RadioButtons
            value={vaultStatusFilter}
            size='small'
            className='px-2 md:px-3'
            onChange={(value: VaultStatusFilter) => setVaultStatusFilter(value)}
            options={[
              { label: 'All', value: 'all' },
              { label: 'Open', value: 'open' },
              { label: 'Closed', value: 'closed' },
            ]}
          />
        </div>

        <VaultFilterSelect
          id='vault-chain-filter'
          label='Chain'
          value={vaultChainFilter}
          onChange={(value) => setVaultChainFilter(value as VaultChainFilter)}
          options={[
            { label: 'All', value: 'all' },
            { label: 'Base', value: 'base' },
            { label: 'Ethereum', value: 'ethereum' },
            { label: 'Arbitrum', value: 'arbitrum' },
            { label: 'BNB Chain', value: 'bnb' },
          ]}
        />

        <VaultFilterSelect
          id='vault-asset-type-filter'
          label='Asset Type'
          value={vaultAssetTypeFilter}
          onChange={(value) =>
            setVaultAssetTypeFilter(value as VaultAssetTypeFilter)
          }
          options={[
            { label: 'All', value: 'all' },
            { label: 'Ethereum', value: 'ethereum' },
            { label: 'Bitcoin', value: 'bitcoin' },
            { label: 'Stable', value: 'stable' },
          ]}
        />

        <div className='mb-1 ml-auto w-fit self-end'>
          <ViewTypeSwitch />
        </div>
      </div>

      <div className='grid w-full grid-cols-1 gap-4'>
        <div
          className={cx('w-full overflow-x-auto pb-1', {
            invisible: !isMobile && viewType === 'table',
            visible: isMobile,
          })}
        >
          <SortControls />
        </div>
      </div>
    </div>
  );
};

interface VaultFilterSelectProps {
  id: string;
  label: string;
  value: string;
  options: { label: string; value: string }[];
  onChange: (value: string) => void;
}

const VaultFilterSelect = (props: VaultFilterSelectProps) => {
  const { id, label, value, options, onChange } = props;

  return (
    <label htmlFor={id} className='flex min-w-[132px] flex-col gap-2'>
      <Body3 className='text-nowrap text-white-500'>{label}</Body3>
      <div className='relative'>
        <select
          id={id}
          value={value}
          className='rounded-main h-9 w-full cursor-pointer appearance-none border border-[#595959] bg-[#111111] px-3 pr-9 text-sm text-white outline-none transition-colors hover:border-[#B5B5B5] focus:border-white'
          onChange={(event) => onChange(event.target.value)}
        >
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <Icon
          icon='expand_more'
          color='#F0F0F0'
          className='pointer-events-none absolute right-2 top-1/2 -translate-y-1/2'
        />
      </div>
    </label>
  );
};

interface SortButtonProps {
  sortDirection: 'asc' | 'desc' | 'default';
  onClick: () => void;
  children: React.ReactNode;
}

const SortButton = (props: SortButtonProps) => {
  const { sortDirection, onClick, children } = props;

  const iconClasses = cx('text-inherit', {
    'rotate-180': sortDirection === 'desc',
    'rotate-0': sortDirection === 'asc',
    hidden: sortDirection === 'default',
  });

  const style = sortDirection !== 'default' ? 'neutral' : 'outline';

  return (
    <Button
      size='small'
      style={style}
      className='hover:bg-[linear-gradient(103deg,_#363636_0%,_#1A1A1A_100%)] hover:text-white'
      onClick={onClick}
    >
      <div className='flex cursor-pointer flex-row items-center gap-1'>
        <Icon
          icon='arrow_upward'
          className={iconClasses}
          color={sortDirection === 'default' ? '#B5B5B5' : '#F0F0F0'}
        />
        {children}
      </div>
    </Button>
  );
};

const SortControls = () => {
  const { getSortDirection, changeSortDirection } = useVaultListContext();

  return (
    <div className='flex flex-row items-center gap-2'>
      <Body3 className='text-nowrap'>Sort by:</Body3>
      <SortButton
        sortDirection={getSortDirection('tvl')}
        onClick={() => changeSortDirection('tvl')}
      >
        TVL
      </SortButton>
      <SortButton
        sortDirection={getSortDirection('apy')}
        onClick={() => changeSortDirection('apy')}
      >
        APY
      </SortButton>
      <SortButton
        sortDirection={getSortDirection('price')}
        onClick={() => changeSortDirection('price')}
      >
        Price
      </SortButton>
      <SortButton
        sortDirection={getSortDirection('position')}
        onClick={() => changeSortDirection('position')}
      >
        Position
      </SortButton>
    </div>
  );
};
